/**
 * DashboardAdminBanner.jsx
 *
 * Shown at the top of AdminOpdViewer when the logged-in user is a
 * DASHBOARD_ADMIN. Explains their role scope, lists what they CAN'T
 * access, and tells them who to contact for elevated access.
 *
 * Dismissible — once closed it stays hidden for 30 days via localStorage
 * so it doesn't re-appear on every visit, but does reappear eventually
 * as a gentle reminder.
 *
 * Usage:
 *   <DashboardAdminBanner />
 *
 * The component reads the role from sessionStorage itself so it can be
 * dropped anywhere without prop-drilling.
 */

import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";

const STORAGE_KEY   = "dashboardAdminBannerDismissedAt";
const REDISPLAY_MS  = 30 * 24 * 60 * 60 * 1000; // 30 days

/* Features visible to MASTER_ADMIN / ADMIN but not DASHBOARD_ADMIN */
const LOCKED_FEATURES = [
  { icon: "📊", label: "Dashboard & analytics"  },
  { icon: "📋", label: "OPD requests"            },
  { icon: "🗂️", label: "Medical records (MRD)"  },
  { icon: "🌐", label: "Website content"         },
  { icon: "👨‍⚕️", label: "Doctor profiles"         },
  { icon: "💊", label: "Medicine database"       },
];

export default function DashboardAdminBanner() {
  /* Read role from sessionStorage (set at login, same pattern as AdminLayout) */
  const admin = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem("adminUser") || "null"); }
    catch { return null; }
  }, []);

  const [visible, setVisible] = useState(false);

  /* Decide whether to show on mount */
  useEffect(() => {
    if (admin?.role !== "DASHBOARD_ADMIN") return;

    const dismissedAt = localStorage.getItem(STORAGE_KEY);
    if (dismissedAt) {
      const elapsed = Date.now() - parseInt(dismissedAt, 10);
      if (elapsed < REDISPLAY_MS) return; // still within the quiet period
    }

    setVisible(true);
  }, [admin]);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setVisible(false);
  };

  /* Don't render anything for non-dashboard-admin roles */
  if (admin?.role !== "DASHBOARD_ADMIN") return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.2 }}
          style={{
            background: "#EFF6FF",
            border: "1px solid #BFDBFE",
            borderRadius: 14,
            padding: "16px 20px",
            fontFamily: "'Inter','Segoe UI',sans-serif",
            position: "relative",
          }}
        >
          {/* Dismiss button */}
          <button
            onClick={dismiss}
            title="Dismiss"
            style={{
              position: "absolute", top: 12, right: 14,
              background: "none", border: "none", cursor: "pointer",
              fontSize: 16, color: "#93C5FD", lineHeight: 1,
              padding: 4, borderRadius: 6,
            }}
            onMouseEnter={e => e.currentTarget.style.color = "#1D4ED8"}
            onMouseLeave={e => e.currentTarget.style.color = "#93C5FD"}
          >
            ✕
          </button>

          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <span style={{ fontSize: 18 }}>👋</span>
            <div>
              <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#1E3A5F" }}>
                Welcome{admin?.name ? `, ${admin.name}` : ""}
              </p>
              <p style={{ margin: 0, fontSize: 12, color: "#3B82F6", marginTop: 1 }}>
                Dashboard Admin · Read-only access
              </p>
            </div>
          </div>

          {/* Role description */}
          <p style={{ margin: "0 0 12px", fontSize: 13, color: "#1E40AF", lineHeight: 1.55 }}>
            Your account has access to the <strong>OPD Live Viewer</strong> — a real-time
            read-only view of each doctor's patient queue. No patient data can be edited
            from this panel.
          </p>

          {/* Locked features grid */}
          <div style={{ marginBottom: 14 }}>
            <p style={{
              margin: "0 0 8px", fontSize: 11, fontWeight: 700,
              color: "#93C5FD", letterSpacing: "0.07em", textTransform: "uppercase",
            }}>
              Not included in your role
            </p>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
              gap: 6,
            }}>
              {LOCKED_FEATURES.map(f => (
                <div key={f.label} style={{
                  display: "flex", alignItems: "center", gap: 7,
                  background: "#DBEAFE", borderRadius: 8,
                  padding: "6px 10px",
                }}>
                  <span style={{ fontSize: 13 }}>{f.icon}</span>
                  <span style={{ fontSize: 12, color: "#1E40AF" }}>{f.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Contact line */}
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            background: "#DBEAFE", borderRadius: 10, padding: "10px 14px",
          }}>
            <span style={{ fontSize: 14 }}>🔑</span>
            <p style={{ margin: 0, fontSize: 12, color: "#1E40AF", lineHeight: 1.5 }}>
              Need access to other sections? Contact your{" "}
              <strong>Master Admin</strong> to update your role.
            </p>
          </div>

        </motion.div>
      )}
    </AnimatePresence>
  );
}
